/**
 * 拆分扫描对象锁·纯模型（硬锁/软锁分层 + 并行防踩踏）
 *
 * 背景（0908 QA 并行拆分 + 0909 锁语义收口）：
 * - 旧模型只有一个锁清单（Record<覆盖层key, objects[]>），'main' 单例 key 在并行主拆分下
 *   互相整键覆写/整键清除——后发起的任务把在途任务的源图锁挤掉、先收敛的任务把在途任务
 *   的锁一并清掉。本模块把登记语义改为「按 key 追加 / 按对象扣除」，并提供死引用 GC。
 * - 旧模型对被扫对象一刀切 selectable/evented=false，用户反馈「扫描中源图完全不可交互」。
 *   收口口径：**图组类对象（带 SPLIT_GROUP_TAG）维持硬锁**（re-split 父组/修补组/落地结果组
 *   的重建与替换依赖组结构，中途解组=付费结果丢弃）；**纯图片源图降级为软锁**——只登记不改
 *   fabric 标记，用于入口耦合判定（拆分置灰/删除守卫），选中/拖动/详情正常放行。
 *
 * 本文件为纯函数：输入锁清单 refs 输出新 buckets，不持有状态；Pinia 侧在 toolbar.ts 薄委托。
 */
import { toRaw, type Ref } from 'vue';
import { SPLIT_GROUP_TAG } from '@/views/workspace/board/detail/components/ImageContextToolbar/GameUiSplitterPanel/constants';

/** 硬锁（selectable/evented=false，SplitScanOverlay 消费）与软锁（仅入口判定）的分桶 */
export interface ScanLockBuckets {
  hard: Record<string, any[]>;
  soft: Record<string, any[]>;
}

export type ScanLockRefs = {
  hard: Ref<Record<string, any[]>>;
  soft: Ref<Record<string, any[]>>;
};

/** 登记语义：'main' 主扫描跨流程累计（append，配合按对象扣除）；派生/修补 key 单命令独占（replace） */
export type ScanLockWriteMode = 'append' | 'replace';

function bucketsOf(refs: ScanLockRefs): ScanLockBuckets {
  return { hard: refs.hard.value, soft: refs.soft.value };
}

function applyBuckets(refs: ScanLockRefs, next: ScanLockBuckets) {
  refs.hard.value = next.hard;
  refs.soft.value = next.soft;
}

/** 归一化身份键：锁清单存的是 fabric 对象引用，与 canvas.getObjects()/getActiveObject()
 *  比较必须走 toRaw（reactive 深代理会让引用比较永远失配，见 toolbar.splitScanLockedObjectSet 注释） */
function rawId(obj: any): any {
  return obj ? toRaw(obj) : null;
}

/**
 * 按对象类型分类锁强度：图组 → 硬锁；纯图片源图等 → 软锁。
 * 判据用组 tag 而非调用方传参——所有登记路径（主拆分默认源图/继续拆分父组/派生恢复组/修补组/
 * 落地结果组）收敛到同一 choke point，未来新增入口不会漏分类。
 */
export function classifyScanLockObject(obj: any): 'hard' | 'soft' {
  return obj?.[SPLIT_GROUP_TAG] ? 'hard' : 'soft';
}

function upsertBucketList(
  bucket: Record<string, any[]>,
  key: string,
  mode: ScanLockWriteMode,
  objects: any[],
): Record<string, any[]> {
  const prev = mode === 'replace' ? [] : (bucket[key] ?? []);
  // 追加去重（raw 身份）：同对象重复登记（恢复链路重入）不产生重复条目
  const seen = new Set(prev.map(rawId));
  const merged = [...prev];
  for (const obj of objects) {
    if (!obj) continue;
    const id = rawId(obj);
    if (seen.has(id)) continue;
    seen.add(id);
    merged.push(obj);
  }
  const next = { ...bucket };
  if (merged.length > 0) next[key] = merged;
  else delete next[key];
  return next;
}

function writeBuckets(
  buckets: ScanLockBuckets,
  key: string,
  mode: ScanLockWriteMode,
  hardList: any[],
  softList: any[],
): ScanLockBuckets {
  return {
    hard: upsertBucketList(buckets.hard, key, mode, hardList),
    soft: upsertBucketList(buckets.soft, key, mode, softList),
  };
}

/** 统一登记：按 tag 分硬/软桶写入；replace 模式先清空该 key 旧列表（硬/软两桶同 key 互斥使用） */
export function registerScanLocks(
  refs: ScanLockRefs,
  key: string,
  objects: any[],
  mode: ScanLockWriteMode = 'append',
) {
  const hardList: any[] = [];
  const softList: any[] = [];
  for (const obj of objects) {
    if (!obj) continue;
    (classifyScanLockObject(obj) === 'hard' ? hardList : softList).push(obj);
  }
  applyBuckets(refs, writeBuckets(bucketsOf(refs), key, mode, hardList, softList));
}

/** 按对象扣除（raw 身份匹配）：流程收尾只清自己登记的对象，不动并行在途任务的锁 */
export function unregisterScanLocks(refs: ScanLockRefs, key: string, objects: any[]) {
  const removeIds = new Set(objects.map(rawId).filter(Boolean));
  const dropIds = (list: any[] | undefined) => {
    if (!list) return undefined;
    const kept = list.filter((o) => !removeIds.has(rawId(o)));
    return kept.length > 0 ? kept : undefined;
  };
  const pick = (bucket: Record<string, any[]>) => {
    const record = { ...bucket };
    const next = dropIds(record[key]);
    if (next) record[key] = next;
    else delete record[key];
    return record;
  };
  applyBuckets(refs, { hard: pick(refs.hard.value), soft: pick(refs.soft.value) });
}

/**
 * 死引用 GC：清掉不在画布上的锁对象（画布重建/对象删除后的残留登记）。
 * 只在发起新扫描时调用——在途任务的活对象都在画布上，不会被误清。
 */
export function pruneScanLocks(refs: ScanLockRefs, liveObjects: any[]) {
  const live = new Set((liveObjects ?? []).map(rawId));
  const prune = (bucket: Record<string, any[]>) => {
    const next: Record<string, any[]> = {};
    for (const [key, list] of Object.entries(bucket)) {
      const kept = (list ?? []).filter((o) => live.has(rawId(o)));
      if (kept.length > 0) next[key] = kept;
    }
    return next;
  };
  applyBuckets(refs, { hard: prune(refs.hard.value), soft: prune(refs.soft.value) });
}

/** 跨 key 并集（toRaw 归一化），供守卫集合计算 */
export function collectScanLockSet(record: Record<string, any[]> | undefined): Set<any> {
  const set = new Set<any>();
  for (const list of Object.values(record ?? {})) {
    for (const obj of list ?? []) if (obj) set.add(rawId(obj));
  }
  return set;
}
